import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { Gift, Copy, Check, Users } from "lucide-react";

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data: d } = await api.get("/referrals/me"); setData(d); }
    catch (e) { console.error("Failed to load referrals:", e); }
  };
  useEffect(() => { load(); }, []);

  const link = data ? `${window.location.origin}/login?ref=${data.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Could not copy the link"); }
  };

  const redeem = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/referrals/redeem", { code });
      toast.success(`Code applied — ${r.referrer} invited you`, {
        description: `Fund ${fmtUSD(r.min_deposit_usd)} and you both get ${fmtUSD(r.reward_usd)}`,
      });
      setCode(""); await load();
    } catch (e) { toast.error("Could not apply code", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <Card className="p-5 shadow-premium" data-testid="referral-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium"><Gift className="h-4 w-4" />Invite &amp; earn</div>
        <Badge variant="secondary" className="numeric text-[10px]" data-testid="referral-earned">
          {fmtUSD(data.earned_usd)} earned
        </Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        You both get <span className="font-medium text-foreground">{fmtUSD(data.reward_usd)}</span> once your friend funds{" "}
        {fmtUSD(data.min_deposit_usd)}.
      </p>

      <div className="rounded-xl border bg-secondary/50 p-3">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Your code</div>
        <div className="numeric text-xl font-bold tracking-[0.15em]" data-testid="referral-code">{data.code}</div>
        <Button size="sm" variant="secondary" className="w-full mt-3 gap-1.5" onClick={copy} data-testid="referral-copy">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy invite link"}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        {[["Invited", data.invited], ["Funded", data.funded], ["Pending", fmtUSD(data.pending_usd)]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-secondary/60 py-2">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{l}</div>
            <div className="numeric text-sm font-semibold">{v}</div>
          </div>
        ))}
      </div>

      {data.referrals.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="referral-list">
          {data.referrals.slice(0, 5).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs" data-testid={`referral-row-${r.id}`}>
              <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-muted-foreground" />{r.name}</span>
              <Badge variant={r.status === "funded" ? "default" : "outline"} className="text-[10px] capitalize">{r.status}</Badge>
            </div>
          ))}
        </div>
      )}

      {!data.referred_by && (
        <div className="mt-4 pt-3 border-t space-y-2" data-testid="referral-redeem">
          <div className="text-xs text-muted-foreground">Were you invited? Enter their code before your first deposit.</div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="MX7K2QP4"
              className="numeric tracking-widest" data-testid="referral-code-input" />
            <Button size="sm" onClick={redeem} disabled={busy || code.length < 4} data-testid="referral-redeem-submit">Apply</Button>
          </div>
        </div>
      )}
    </Card>
  );
};
